// Shared navigation config.
// Single source of truth for the module -> child-route grouping, consumed by both the left
// sidebar (VerticalNavbarNew) and the center module-tab UI in the PageHeader so they never drift.

import { FINOPS_TAB_PATHS } from '@root/constants/finOpsTabs';
import { INVENTORY_TAB_PATHS } from '@root/constants/inventoryTabs';
import {
  AI_SECURITY_TABS,
  API_SECURITY_TABS,
  APPLICATION_SECURITY_TABS,
  ATTACK_SURFACE_TABS,
  DATABASE_SECURITY_TABS,
  IDENTITY_TABS,
  K8S_RUNTIME_TABS,
  K8S_VULNERABILITIES_TABS,
  REMEDIATION_HUB_TABS,
  THREAT_TABS,
  WORKLOAD_TABS,
} from '@root/constants/moduleTabs';
import { WORKSPACE_TAB_PATHS } from '@root/constants/workspaceTabs';

export const PREDEFINE_ROUTE_MAP = {
  '/dashboard': 'Dashboard',
  '/data-security': 'Data Security',
  '/attack-surface': 'Attack Surface',
  '/attack-path-analysis': 'Attack Paths',
  '/inventory': 'Inventory',
  // Explorer is the `?tab=explorer` tab of `/inventory`; `/explorer` only survives as a redirect for
  // old links, so it maps to the module that now owns it.
  '/explorer': 'Inventory',
  '/scan-history': 'Insights',
  '/compliance': 'Insights',
  '/risk': 'Insights',
  '/rules': 'Insights',
  '/security-score': 'Insights',
  '/notifications': 'Insights',
  '/exceptions': 'Insights',
  '/discovery': 'Insights',
  '/identity': 'Identity',
  '/vulnerabilities': 'Workload',
  '/vulnerability/management': 'Workload',
  '/workload': 'Workload',
  // `/events/*` and `/cdr` only survive as redirects into `/threat`; they map to the module that
  // owns them so the sidebar stays highlighted while the redirect resolves.
  '/events': 'Threat',
  '/application-security': 'Application Security',
  '/cdr': 'Threat',
  '/threat': 'Threat',
  '/code': 'Code Security',
  '/remediation-policy': 'Remediation Hub',
  '/remediation-report': 'Remediation Hub',
  '/environments': 'Workspace',
  '/workspace': 'Workspace',
  '/rbac': 'Workspace',
  '/reports': 'Workspace',
  '/kubernetes-live': 'Kubernetes Live',
  '/integrations': 'Workspace',
  '/ai-security': 'AI Security',
  '/api-security': 'API Security',
  '/database-security': 'Database Security',
  '/finops': 'FinOps',
};

export const ROUTES = [
  { name: 'Dashboard', to: '/dashboard' },
  {
    name: 'Inventory',
    // Inventory is a single route (`/inventory`); its tabs are query parameters, so clicking a tab
    // never remounts the page. See src/constants/inventoryTabs.js.
    children: [
      { name: 'Overview', to: INVENTORY_TAB_PATHS.overview },
      { name: 'Resources', to: INVENTORY_TAB_PATHS.resources },
      { name: 'Key Insights', to: INVENTORY_TAB_PATHS.keyInsight },
      { name: 'Explorer', to: INVENTORY_TAB_PATHS.explorer },
    ],
  },
  {
    name: 'Insights',
    children: [
      { name: 'Scan', to: '/scan-history' },
      { name: 'Compliance', to: '/compliance' },
      { name: 'Risk', to: '/risk' },
      { name: 'Rules', to: '/rules' },
      { name: 'Security Score', to: '/security-score' },
      { name: 'Alerts', to: '/notifications' },
      { name: 'Exceptions', to: '/exceptions' },
      // { name: 'Discovery', to: '/discovery', badge: 'BETA' },
    ],
  },
  { name: 'Data Security', to: '/data-security' },
  { name: 'Attack Paths', to: '/attack-path-analysis' },
  {
    name: 'Identity',
    // Opts into center header tabs despite having more than the default child count.
    hasTabs: true,
    children: [
      { name: 'Overview', to: IDENTITY_TABS.path('overview') },
      { name: 'Human Identity', to: IDENTITY_TABS.path('human-identity') },
      { name: 'Non-Human Identity', to: IDENTITY_TABS.path('non-human-identity') },
    ],
  },
  {
    name: 'Workload',
    children: [
      { name: 'Dashboard', to: WORKLOAD_TABS.path('dashboard') },
      { name: 'Vulnerabilities', to: WORKLOAD_TABS.path('vulnerabilities') },
    ],
  },
  {
    name: 'Threat',
    children: [
      { name: 'Threat Story', to: THREAT_TABS.path('threat-story'), badge: 'BETA' },
      { name: 'Events', to: THREAT_TABS.path('events') },
    ],
  },
  {
    name: 'Application Security',
    children: [
      { name: 'Repository Scanning', to: APPLICATION_SECURITY_TABS.path('repository-scanning') },
      { name: 'Registry Scanning', to: APPLICATION_SECURITY_TABS.path('registry-scanning') },
      { name: 'DAST Scanning', to: APPLICATION_SECURITY_TABS.path('dast-scanning'), badge: 'beta' },
    ],
  },
  {
    name: 'Kubernetes Live',
    children: [
      { name: 'Overview', to: '/kubernetes-live/overview' },
      { name: 'Inventory', to: '/kubernetes-live/inventory' },
      { name: 'Identity', to: '/kubernetes-live/identity' },
      { name: 'Compliance', to: '/kubernetes-live/compliance' },
      { name: 'Runtime Detection', to: '/kubernetes-live/runtime' },
      { name: 'Enforcement Hub', to: '/kubernetes-live/enforcement', badge: 'beta' },
      { name: 'Vulnerabilities', to: '/kubernetes-live/vulnerabilities' },
      { name: 'Seccomp', to: '/kubernetes-live/seccomp' },
      { name: 'Network', to: '/kubernetes-live/network', badge: 'BETA' },
      { name: 'Risk Acceptance', to: '/kubernetes-live/risk-acceptance' },
      { name: 'Malware', to: '/kubernetes-live/malware' },
      { name: 'Events', to: '/kubernetes-live/events?isKubernetes=true' },
      { name: 'Action History', to: '/kubernetes-live/suppressed' },

      { name: 'Scan History', to: '/kubernetes-live/scan-history', badge: 'BETA' },
      { name: 'Image Scout', to: '/kubernetes-live/image-scout', badge: 'BETA' },
    ],
  },
  {
    name: 'AI Security',
    badge: 'BETA',
    children: [
      { name: 'Overview', to: AI_SECURITY_TABS.path('overview') },
      { name: 'Inventory', to: AI_SECURITY_TABS.path('inventory') },
      { name: 'Runtime Threat', to: AI_SECURITY_TABS.path('runtime-threat') },
      { name: 'Findings', to: AI_SECURITY_TABS.path('findings') },
    ],
  },
  {
    name: 'API Security',
    badge: 'BETA',
    children: [
      { name: 'Overview', to: API_SECURITY_TABS.path('overview') },
      { name: 'API Inventory', to: API_SECURITY_TABS.path('inventory') },
      { name: 'Findings', to: API_SECURITY_TABS.path('findings') },
      // Configured routes never seen in traffic; the route keeps its old `shadow-apis` key.
      { name: 'Zombie APIs', to: API_SECURITY_TABS.path('shadow-apis') },
      // { name: 'Compliance', to: '/api-security/compliance' },
      // { name: 'Attack Path', to: '/api-security/attack-path' },
    ],
  },
  {
    name: 'Database Security',
    badge: 'BETA',
    children: [
      { name: 'Overview', to: DATABASE_SECURITY_TABS.path('overview'), badge: 'New' },
      { name: 'Exceptions', to: DATABASE_SECURITY_TABS.path('exceptions') },
    ],
  },
  {
    name: 'Remediation Hub',
    children: [
      { name: 'Policies', to: REMEDIATION_HUB_TABS.path('policies') },
      { name: 'Reports', to: REMEDIATION_HUB_TABS.path('reports') },
    ],
  },
  {
    name: 'FinOps',
    // Like Inventory, FinOps keeps one route mounted and uses `?tab=` for its views.
    children: [
      { name: 'Cost Optimization Opportunities', to: FINOPS_TAB_PATHS.overview },
      { name: 'AI cost optimization', to: FINOPS_TAB_PATHS.aiCostOptimization },
      { name: 'Cloud Cost Management Stages', to: FINOPS_TAB_PATHS.cloudCostManagementStages },
    ],
  },
  {
    name: 'Workspace',
    // Workspace follows the same single-route, query-tab pattern as Inventory.
    children: [
      { name: 'Environments', to: WORKSPACE_TAB_PATHS.environments },
      { name: 'RBAC', to: WORKSPACE_TAB_PATHS.rbac },
      { name: 'Reports', to: WORKSPACE_TAB_PATHS.reports },
      { name: 'Integrations', to: WORKSPACE_TAB_PATHS.integrations },
    ],
  },
];

/**
 * Center header tabs for a single page inside a module too large to surface its children as tabs
 * (Kubernetes Live). They share the module tab bar but never appear in the sidebar, which keeps
 * linking the page itself.
 */
export const PAGE_TABS = [
  {
    // Not in the sidebar (Attack Paths covers it); the page and its tabs stay for existing links. Like Inventory, it keeps
    // one route mounted and uses `?tab=` for its views.
    name: 'Attack Surface',
    children: [
      { name: 'Dashboard', to: ATTACK_SURFACE_TABS.path('dashboard') },
      { name: 'Findings', to: ATTACK_SURFACE_TABS.path('findings') },
      { name: 'Playbooks', to: ATTACK_SURFACE_TABS.path('playbooks') },
    ],
  },
  {
    name: 'Kubernetes Vulnerabilities',
    children: [
      { name: 'Dashboard', to: K8S_VULNERABILITIES_TABS.path('dashboard') },
      // Pins only `activeTab`, so the tab stays lit on every Vulnerabilities sub tab.
      { name: 'Vulnerabilities', to: K8S_VULNERABILITIES_TABS.path('vulnerabilities') },
      { name: 'SBOM', to: K8S_VULNERABILITIES_TABS.path('sbom') },
      { name: 'Host', to: K8S_VULNERABILITIES_TABS.path('host') },
    ],
  },
  {
    name: 'Kubernetes Runtime Detection',
    children: [
      { name: 'Threat Story', to: K8S_RUNTIME_TABS.path('threat-story'), badge: 'BETA' },
      { name: 'Runtime Detection', to: K8S_RUNTIME_TABS.path('runtime-detection') },
    ],
  },
];

/**
 * A module surfaces its children as center header tabs (and therefore as a single direct link in
 * the left sidebar, rather than a popover of children) when it has 2 to 5 children, or when it
 * sets `hasTabs: true` to opt in explicitly regardless of child count.
 *
 * @param {{ children?: Array<unknown>, hasTabs?: boolean }} route a ROUTES entry
 * @returns {boolean}
 */
export function moduleHasTabs(route) {
  const count = route?.children?.length || 0;
  if (count === 0) return false;
  if (route?.hasTabs) return true;
  return count >= 2 && count <= 5;
}

/**
 * Resolve the active module's child routes for the center tab UI.
 *
 * The active module is found by matching the current location against the `to` of each module's
 * children (longest pathname match wins), which is robust to multi-segment routes such as
 * `/vulnerability/management`. Returns tabs only when that module qualifies per `moduleHasTabs`;
 * otherwise null, so single-route and large modules (and non-module pages) render no tabs.
 *
 * A child's `to` may pin query parameters (e.g. Inventory's `/inventory?tab=resources`, which share
 * one pathname). Such a child only matches when every parameter it pins equals the one in the
 * current URL, and ties on pathname length are broken by the number of matched parameters. When no
 * query-pinning child matches — a bare `/inventory` with no `tab` — the first child whose pathname
 * matches is used, so the tab bar still renders and points at the module's default tab.
 *
 * When the matched module does not qualify, the PAGE_TABS groups are matched the same way, so a
 * page inside a large module (Kubernetes Live) can still render its own tabs.
 *
 * @param {string} pathname current location.pathname
 * @param {string} [search] current location.search
 * @returns {{ moduleName: string, tabs: Array<{name: string, to: string}>, activeTo: string } | null}
 */
export function getActiveModuleTabs(pathname, search = '') {
  if (!pathname) return null;
  const path = pathname.split('?')[0];
  const currentParams = new URLSearchParams(search);

  const active = findActiveChild(ROUTES, path, currentParams);
  if (active && moduleHasTabs(active.module)) {
    return { moduleName: active.module.name, tabs: active.module.children, activeTo: active.child.to };
  }

  const page = findActiveChild(PAGE_TABS, path, currentParams);
  if (!page) return null;

  return { moduleName: page.module.name, tabs: page.module.children, activeTo: page.child.to };
}

function findActiveChild(groups, path, currentParams) {
  let best = null; // { module, child, len, matchedParams }
  let pathOnlyBest = null; // fallback used when no child's pinned params match
  for (const route of groups) {
    if (!route.children) continue;
    for (const child of route.children) {
      const [childPath, childQuery = ''] = child.to.split('?');
      if (path !== childPath && !path.startsWith(childPath + '/')) continue;

      if (!pathOnlyBest || childPath.length > pathOnlyBest.len) {
        pathOnlyBest = { module: route, child, len: childPath.length };
      }

      const childParams = [...new URLSearchParams(childQuery)];
      if (childParams.some(([key, value]) => currentParams.get(key) !== value)) continue;

      const candidate = { module: route, child, len: childPath.length, matchedParams: childParams.length };
      if (
        !best ||
        candidate.len > best.len ||
        (candidate.len === best.len && candidate.matchedParams > best.matchedParams)
      ) {
        best = candidate;
      }
    }
  }

  return best || pathOnlyBest;
}
